import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiMapPin,
  FiSearch,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { safariTrips } from "../data/safariTrips";
import gorillaImage from "../assets/gorilla.jpg";
import lionsImage from "../assets/lions.jpg";
import craneImage from "../assets/CrestedCranes.jpg";

const slides = [
  {
    id: 1,
    image: gorillaImage,
    eyebrow: "Gorilla Trekking",
    title: "Experience Uganda's Wild Heart",
    subtitle:
      "Private gorilla safaris, forest walks, and conservation-led journeys through East Africa.",
    location: "Bwindi Impenetrable Forest",
  },
  {
    id: 2,
    image: lionsImage,
    eyebrow: "Classic Wildlife",
    title: "Follow The Big Game Across The Savannah",
    subtitle:
      "Tailor-made safari routes through iconic parks, guided by experts who know the land.",
    location: "Queen Elizabeth & Murchison Falls",
  },
  {
    id: 3,
    image: craneImage,
    eyebrow: "Birding & Nature",
    title: "Discover Rare Wildlife In Remarkable Places",
    subtitle:
      "From crested cranes to primates, build a journey around the species you dream of seeing.",
    location: "Wetlands, Forests & Lakes",
  },
];

const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6500);

    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setQuery(value);

    if (!value.trim()) {
      setMatches([]);
      return;
    }

    const filtered = safariTrips
      .filter(
        (trip) =>
          trip.destination?.toLowerCase().includes(value.toLowerCase()) ||
          trip.title?.toLowerCase().includes(value.toLowerCase())
      )
      .slice(0, 5);

    setMatches(filtered);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (matches[0]) {
      navigate(`/safari/${matches[0].id}`);
      return;
    }

    navigate("/tours");
  };

  const handleSelect = (tripId) => {
    navigate(`/safari/${tripId}`);
    setQuery("");
    setMatches([]);
  };

  const imageVariants = {
    enter: (slideDirection) => ({
      opacity: 0,
      scale: 1.08,
      x: slideDirection > 0 ? 80 : -80,
    }),
    center: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { duration: 1.1, ease: "easeInOut" },
    },
    exit: (slideDirection) => ({
      opacity: 0,
      scale: 1.04,
      x: slideDirection > 0 ? -80 : 80,
      transition: { duration: 0.8, ease: "easeInOut" },
    }),
  };

  const contentVariants = {
    enter: { opacity: 0, y: 28 },
    center: { opacity: 1, y: 0, transition: { duration: 0.65, delay: 0.15 } },
    exit: { opacity: 0, y: -18, transition: { duration: 0.35 } },
  };

  const activeSlide = slides[current];

  return (
    <section className="relative min-h-[92vh] overflow-hidden bg-[#061f16] font-[Poppins] text-white">
      <div className="absolute inset-0">
        <AnimatePresence custom={direction} mode="wait">
          <motion.img
            key={activeSlide.id}
            src={activeSlide.image}
            alt={activeSlide.title}
            custom={direction}
            variants={imageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-[#03140d]/95 via-[#03140d]/68 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#061f16] to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl items-center px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pt-32">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSlide.id}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731] backdrop-blur">
                  {activeSlide.eyebrow}
                </div>

                <h1 className="max-w-4xl text-4xl font-extrabold uppercase leading-tight tracking-normal text-white sm:text-5xl lg:text-6xl">
                  {activeSlide.title}
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-8 text-white/80 sm:text-lg">
                  {activeSlide.subtitle}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/80">
                  <span className="inline-flex items-center gap-2">
                    <FiMapPin className="text-[#F5B731]" />
                    {activeSlide.location}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FiCalendar className="text-[#F5B731]" />
                    Custom departures all year
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>

            <form
              onSubmit={handleSubmit}
              className="relative mt-9 max-w-2xl rounded-sm bg-white p-2 shadow-2xl shadow-black/30"
            >
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#466458]" />
                  <input
                    type="text"
                    value={query}
                    onChange={handleChange}
                    placeholder="Search gorilla, wildlife, destination..."
                    className="h-14 w-full rounded-sm bg-[#f6f3ea] pl-11 pr-4 text-sm font-medium text-[#082f1f] outline-none ring-1 ring-transparent transition focus:ring-[#F5B731]"
                  />
                </div>
                <button
                  type="submit"
                  className="h-14 rounded-sm bg-[#F5B731] px-7 text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f] transition hover:bg-[#082f1f] hover:text-white"
                >
                  Find Safari
                </button>
              </div>

              {matches.length > 0 && (
                <ul className="absolute left-2 right-2 top-[calc(100%-8px)] z-30 overflow-hidden rounded-sm border border-[#eadfbe] bg-white text-[#082f1f] shadow-xl">
                  {matches.map((trip) => (
                    <li key={trip.id} className="border-b border-[#eadfbe] last:border-b-0">
                      <button
                        type="button"
                        onClick={() => handleSelect(trip.id)}
                        className="flex w-full flex-col px-4 py-3 text-left transition hover:bg-[#f6f3ea]"
                      >
                        <span className="text-sm font-bold">{trip.title}</span>
                        <span className="text-xs text-[#607568]">{trip.destination}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </form>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/tours"
                className="rounded-sm bg-[#F5B731] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f] shadow-lg transition hover:bg-white"
              >
                Explore Tours
              </Link>
              <Link
                to="/contact"
                className="rounded-sm border border-white/35 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#F5B731] hover:text-[#F5B731]"
              >
                Plan My Trip
              </Link>
            </div>
          </div>

          <div className="hidden justify-end lg:flex">
            <div className="w-full max-w-md border border-white/15 bg-white/10 p-5 backdrop-blur-md">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#F5B731]">
                Wildlife Travel
              </p>
              <div className="mt-5 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-extrabold">12+</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.12em] text-white/65">
                    Parks
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-extrabold">24/7</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.12em] text-white/65">
                    Support
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-extrabold">100%</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.12em] text-white/65">
                    Tailored
                  </p>
                </div>
              </div>
              <p className="mt-6 border-t border-white/15 pt-5 text-sm leading-7 text-white/75">
                Travel with guides who understand wildlife behavior, park timing,
                permits, and the small details that make a safari feel effortless.
              </p>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prevSlide}
        className="absolute bottom-8 right-24 z-20 grid h-11 w-11 place-items-center rounded-sm border border-white/25 bg-black/20 text-white backdrop-blur transition hover:border-[#F5B731] hover:bg-[#F5B731] hover:text-[#082f1f]"
        aria-label="Previous hero slide"
      >
        <FiArrowLeft />
      </button>
      <button
        type="button"
        onClick={nextSlide}
        className="absolute bottom-8 right-8 z-20 grid h-11 w-11 place-items-center rounded-sm border border-white/25 bg-black/20 text-white backdrop-blur transition hover:border-[#F5B731] hover:bg-[#F5B731] hover:text-[#082f1f]"
        aria-label="Next hero slide"
      >
        <FiArrowRight />
      </button>

      <div className="absolute bottom-8 left-4 z-20 flex items-center gap-3 sm:left-6 lg:left-8">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => {
              setDirection(index > current ? 1 : -1);
              setCurrent(index);
            }}
            className={`h-[3px] rounded-full transition-all duration-300 ${
              index === current ? "w-12 bg-[#F5B731]" : "w-7 bg-white/45"
            }`}
            aria-label={`Go to ${slide.eyebrow} slide`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
